import { Component } from '@theme/component';

/** Texto visible traducible (window.tmI18n); si falta, vale el español. */
function tr(key, fallback, vars) {
  const t = (window.tmI18n && window.tmI18n[key]) || fallback;
  return vars ? t.replace(/\{\{(\w+)\}\}/g, (_, n) => (vars[n] !== undefined ? vars[n] : '')) : t;
}


/**
 * El rectángulo del diseño original que se va a imprimir, en porcentaje.
 *
 * Al escalar en modo cover sobre la pared sobra dibujo en un solo eje: el
 * horizontal si la pared es más estrecha de proporción que el diseño, el
 * vertical si es más apaisada. El encuadre decide dónde cae la ventana dentro
 * de lo que sobra.
 *
 * Vive aquí fuera y se exporta porque hay dos sitios donde el cliente encuadra
 * un mural —esta vista de pared y el modal del catálogo— y la geometría es la
 * misma en los dos: el marco toma la proporción de la pared y la imagen se
 * escala en cover. Tener la cuenta escrita dos veces era pedir que se
 * separasen.
 *
 * @param {number} wallRatio - ancho/alto de la pared
 * @param {number} designRatio - ancho/alto del diseño
 * @param {number} focusX - encuadre horizontal, 0 a 100
 * @param {number} focusY - encuadre vertical, 0 a 100
 */
export function cropRect(wallRatio, designRatio, focusX, focusY) {
  const anchoUsado = wallRatio > designRatio ? 1 : wallRatio / designRatio;
  const altoUsado = wallRatio > designRatio ? designRatio / wallRatio : 1;

  const izquierda = (1 - anchoUsado) * (focusX / 100);
  const arriba = (1 - altoUsado) * (focusY / 100);

  const pct = (n) => Math.round(n * 1000) / 10;
  return {
    x1: pct(izquierda),
    x2: pct(izquierda + anchoUsado),
    y1: pct(arriba),
    y2: pct(arriba + altoUsado),
    usado: Math.round(anchoUsado * altoUsado * 100),
  };
}

/**
 * Vista de pared de la ficha de producto.
 *
 * El marco tiene un tamaño fijo y hace de habitación. Dentro se dibuja la
 * pared del cliente: un rectángulo con su proporción, del mayor tamaño que
 * quepa, con el diseño escalado para llenarlo. Alrededor, y a la misma escala,
 * se ve en sombra el resto del diseño: lo que se queda fuera de la pared.
 *
 * Las dos cosas juntas importan. Si solo se enseña la pared, el cliente no ve
 * cuánto dibujo pierde. Si solo se enseña el diseño entero con una ventana
 * encima —como estaba antes—, pedir más metros enseñaba menos mural: al pasar
 * de 300 a 700 cm de ancho los m² subían de 4,5 a 10,5 y el dibujo visible
 * caía de 384k a 165k píxeles. Un mural a medida se ESCALA a la pared, y lo
 * único que se recorta es la diferencia de proporción.
 *
 * No calcula precios: emite `mural:dimensions` en `document` y la calculadora
 * de la columna derecha lo recoge.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} frame
 * @property {HTMLElement} wall
 * @property {HTMLImageElement} image
 * @property {HTMLElement} ghost
 * @property {HTMLImageElement} ghostImage
 * @property {HTMLElement} [wallLabel]
 * @property {HTMLElement} [coverage]
 * @property {HTMLInputElement} widthInput
 * @property {HTMLInputElement} heightInput
 * @property {HTMLButtonElement[]} [thumbs]
 * @property {HTMLButtonElement} [mirrorButton]
 *
 * @extends {Component<Refs>}
 */
class MuralWallPreview extends Component {
  requiredRefs = ['frame', 'wall', 'image', 'ghost', 'ghostImage', 'widthInput', 'heightInput'];

  /** Encuadre, en el formato de object-position. */
  focusX = 50;
  focusY = 50;

  /** 'design' dibuja la pared a escala; 'room' enseña la foto de ambiente. */
  view = 'design';

  /**
   * Volteo horizontal del diseño. Se aplica con CSS (transform: scaleX(-1))
   * tanto a la imagen dentro de la pared como al fantasma, para que las dos
   * sigan casando igual que sin voltear. Viaja tal cual en "mural:dimensions"
   * -- la calculadora lo guarda y lo manda como propiedad "Espejo" del
   * pedido, que es lo que de verdad le importa a producción.
   */
  mirrored = false;

  /** Unidad en la que el cliente escribe las medidas: 'cm' o 'in'. Siempre se publican en cm. */
  unit = 'cm';

  connectedCallback() {
    super.connectedCallback();

    this.designRatio = Number(this.dataset.designRatio) || 1.5;

    this.resizeObserver = new ResizeObserver(() => this.#draw());
    this.resizeObserver.observe(this.refs.frame);

    this.#setupDrag();
    // EE. UU. entra en pulgadas; el resto, en centimetros. Se puede cambiar.
    if (this.dataset.defaultUnit === 'in') this.selectLengthUnit({ unit: 'in' });
    else this.sync();
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    super.disconnectedCallback?.();
  }

  /** Medida escrita, siempre en cm (los demas componentes solo hablan cm). */
  #cm(input) {
    const v = Number(input.value);
    const f = { cm: 1, in: 2.54, ft: 30.48 }[this.unit] ?? 1;
    return Math.round(v * f * 10) / 10;
  }

  /** Cambia la unidad de los dos campos (cm / in) convirtiendo lo que hay escrito. */
  selectLengthUnit({ unit }) {
    if (unit !== 'cm' && unit !== 'in' && unit !== 'ft') return;
    const { widthInput, heightInput } = this.refs;
    const wCm = this.#cm(widthInput) || 200;
    const hCm = this.#cm(heightInput) || 150;
    this.unit = unit;
    const cfg = {
      cm: { w: [100, 700], h: [100, 400], step: 10 },
      in: { w: [40, 275], h: [40, 157], step: 1 },
      ft: { w: [3.3, 22.9], h: [3.3, 13.1], step: 0.1 },
    }[unit];
    const conv = (cm) => String(unit === 'ft' ? Math.round(cm / 30.48 * 10) / 10 : Math.round(unit === 'in' ? cm / 2.54 : cm));
    widthInput.value = conv(wCm);
    heightInput.value = conv(hCm);
    widthInput.min = String(cfg.w[0]); widthInput.max = String(cfg.w[1]); widthInput.step = String(cfg.step);
    heightInput.min = String(cfg.h[0]); heightInput.max = String(cfg.h[1]); heightInput.step = String(cfg.step);
    for (const el of this.querySelectorAll('[data-unit-only]')) el.hidden = !el.dataset.unitOnly.split(' ').includes(unit);
    for (const b of this.querySelectorAll('[data-length-unit]')) {
      const on = b.dataset.lengthUnit === unit;
      b.classList.toggle('is-selected', on);
      b.setAttribute('aria-pressed', String(on));
    }
    this.sync();
  }

  /** "6 ft 7 in" a partir de cm. */
  #pies(cm) {
    const totalIn = Math.round(cm / 2.54);
    return Math.floor(totalIn / 12) + ' ft ' + (totalIn % 12) + ' in';
  }

  sync() {
    const width = this.#cm(this.refs.widthInput);
    const height = this.#cm(this.refs.heightInput);
    if (!width || !height) return;

    this.#draw();
    // Superficie: en pies cuadrados primero cuando se mide en in/ft, en m² primero en cm.
    const area = this.querySelector('[data-area-hint]');
    if (area) {
      const m2 = (width * height) / 10000;
      const dec = (n) => (document.documentElement.lang.toLowerCase().startsWith('en') ? n : n.replace('.', ','));
      const a = dec(m2.toFixed(2)) + ' m²';
      const b = dec((m2 * 10.7639).toFixed(2)) + ' ft²';
      area.textContent = this.unit === 'cm' ? a + ' · ' + b : b + ' · ' + a;
    }
    const pies = this.querySelector('[data-feet-hint]');
    if (pies) {
      pies.hidden = this.unit !== 'in';
      pies.textContent = '≈ ' + this.#pies(width) + ' × ' + this.#pies(height);
    }

    document.dispatchEvent(
      new CustomEvent('mural:dimensions', {
        detail: {
          width,
          height,
          focus: `${Math.round(this.focusX)}% ${Math.round(this.focusY)}%`,
          crop: cropRect(width / height, this.designRatio, this.focusX, this.focusY),
          mirrored: this.mirrored,
        },
      })
    );
  }

  /** Voltea el diseño en espejo (horizontal). Ver comentario junto a `mirrored` arriba. */
  toggleMirror() {
    this.mirrored = !this.mirrored;
    const { image, ghostImage, mirrorButton } = this.refs;
    const transform = this.mirrored ? 'scaleX(-1)' : '';
    if (image) image.style.transform = transform;
    if (ghostImage) ghostImage.style.transform = transform;
    if (mirrorButton) mirrorButton.setAttribute('aria-pressed', String(this.mirrored));
    this.sync();
  }

  #draw() {
    const { frame, wall, image, ghost, ghostImage, wallLabel, coverage } = this.refs;

    // En modo ambiente la foto llena el marco entero. No se esconde la pared:
    // es el elemento que contiene la imagen, y ocultarla dejaba el marco vacío.
    if (this.view !== 'design') {
      ghost.hidden = true;
      wall.hidden = false;
      wall.style.width = '100%';
      wall.style.height = '100%';
      image.style.objectPosition = '50% 50%';
      if (wallLabel) wallLabel.textContent = '';
      return;
    }

    const box = frame.getBoundingClientRect();
    if (!box.width) return;

    const wallWidth = this.#cm(this.refs.widthInput);
    const wallHeight = this.#cm(this.refs.heightInput);
    if (!wallWidth || !wallHeight) return;

    const margin = 30;
    const wallRatio = wallWidth / wallHeight;

    let w = box.width - margin * 2;
    let h = w / wallRatio;
    if (h > box.height - margin * 2) {
      h = box.height - margin * 2;
      w = h * wallRatio;
    }

    wall.hidden = false;
    wall.style.width = `${Math.round(w)}px`;
    wall.style.height = `${Math.round(h)}px`;
    image.style.objectPosition = `${this.focusX}% ${this.focusY}%`;

    // El fantasma es el mismo diseño a la MISMA escala que dentro de la pared,
    // pero entero. Así lo que asoma por fuera del rectángulo es exactamente el
    // trozo que no se va a imprimir, y se ve en sombra.
    const r = this.designRatio;
    let gw = w;
    let gh = w / r;
    if (gh < h) {
      gh = h;
      gw = h * r;
    }

    // Se alinea con el encuadre: si la imagen está desplazada dentro de la
    // pared, el fantasma se desplaza igual y los dos casan sin costura.
    const left = (box.width - w) / 2 - (gw - w) * (this.focusX / 100);
    const top = (box.height - h) / 2 - (gh - h) * (this.focusY / 100);

    ghost.hidden = false;
    ghost.style.width = `${Math.round(gw)}px`;
    ghost.style.height = `${Math.round(gh)}px`;
    ghost.style.left = `${Math.round(left)}px`;
    ghost.style.top = `${Math.round(top)}px`;
    if (ghostImage.src !== image.src) ghostImage.src = image.src;

    if (wallLabel) {
      wallLabel.textContent = this.unit === 'in'
        ? `${Math.round(this.refs.widthInput.value)} × ${Math.round(this.refs.heightInput.value)} in`
        : this.unit === 'ft'
          ? `${this.refs.widthInput.value} × ${this.refs.heightInput.value} ft`
          : `${Math.round(wallWidth)} × ${Math.round(wallHeight)} cm`;
    }

    const used = wallRatio > r ? r / wallRatio : wallRatio / r;
    const percent = Math.round(used * 100);
    const canPan = percent < 99;

    wall.classList.toggle('is-movable', canPan);
    ghost.classList.toggle('is-empty', !canPan);

    if (coverage) {
      coverage.textContent = canPan
        ? tr('wall_crop', 'La zona clara es lo que se imprime: el {{p}} % del diseño. Arrástrala para elegir qué parte.', { p: percent })
        : tr('wall_fits', tr('wall_fits', tr('wall_fits', tr('wall_fits', 'El diseño encaja completo en tu pared, no se recorta nada.'))));
    }
  }

  /**
   * Cambia el diseño que se dibuja en la pared. Lo usa la página "Imprime tu
   * mural": el cliente sube su imagen y la ve a escala al instante, con la
   * misma pared, el mismo encuadre y las mismas medidas que cualquier mural.
   * Reinicia el encuadre (50 % / 50 %) porque la proporción de la imagen nueva
   * no tiene nada que ver con la anterior.
   *
   * @param {string} src - URL de la imagen (puede ser una URL local de blob)
   * @param {number} ratio - ancho / alto de la imagen
   */
  setDesign(src, ratio) {
    const { image, ghostImage, frame, coverage } = this.refs;
    this.designRatio = Number(ratio) || this.designRatio;
    this.focusX = 50;
    this.focusY = 50;
    this.view = 'design';

    image.src = src;
    ghostImage.src = src;
    image.style.objectPosition = '50% 50%';
    frame.classList.remove('is-room');
    if (coverage) coverage.hidden = false;

    // La miniatura "El diseño" vuelve a ser la imagen subida, no la de ejemplo.
    for (const thumb of this.refs.thumbs ?? []) {
      const active = (thumb.dataset.view ?? 'design') === 'design';
      thumb.classList.toggle('is-selected', active);
      thumb.setAttribute('aria-pressed', String(active));
      if (!active) continue;
      thumb.dataset.image = src;
      thumb.dataset.ratio = String(this.designRatio);
      const img = thumb.querySelector('img');
      if (img) img.src = src;
    }

    this.sync();
  }

  /** @param {PointerEvent} event */
  selectView(event) {
    const button = event.target instanceof Element ? event.target.closest('.mural-wall__thumb') : null;
    if (!(button instanceof HTMLElement)) return;

    for (const thumb of this.refs.thumbs ?? []) {
      const active = thumb === button;
      thumb.classList.toggle('is-selected', active);
      thumb.setAttribute('aria-pressed', String(active));
    }

    const { view = 'design', image, ratio } = button.dataset;
    this.view = view;
    if (image) this.refs.image.src = image;
    if (ratio) this.designRatio = Number(ratio) || this.designRatio;

    this.refs.frame.classList.toggle('is-room', view !== 'design');
    if (this.refs.coverage) this.refs.coverage.hidden = view !== 'design';

    this.#draw();
  }

  openZoom() {
    window.open(this.refs.image.src, '_blank', 'noopener');
  }

  #setupDrag() {
    const { wall, image } = this.refs;
    /** @type {{pointerId: number, startX: number, startY: number, fromX: number, fromY: number} | null} */
    let drag = null;

    wall.addEventListener('pointerdown', (event) => {
      if (this.view !== 'design' || !wall.classList.contains('is-movable')) return;
      drag = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        fromX: this.focusX,
        fromY: this.focusY,
      };
      wall.classList.add('is-dragging');
      // Un pointerdown sintético no tiene puntero activo y esto lanza; sin el
      // try/catch la excepción aborta el resto del gesto.
      try {
        wall.setPointerCapture(event.pointerId);
      } catch {
        /* sin captura se sigue arrastrando igual */
      }
      event.preventDefault();
    });

    wall.addEventListener('pointermove', (event) => {
      if (!drag || event.pointerId !== drag.pointerId) return;
      const box = wall.getBoundingClientRect();
      if (!box.width || !box.height) return;

      // Sentido contrario al dedo, como cualquier recorte de imagen: mover la
      // foto a la izquierda enseña lo que hay a la derecha.
      const deltaX = ((event.clientX - drag.startX) / box.width) * 100;
      const deltaY = ((event.clientY - drag.startY) / box.height) * 100;

      this.focusX = Math.min(100, Math.max(0, drag.fromX - deltaX));
      this.focusY = Math.min(100, Math.max(0, drag.fromY - deltaY));
      image.style.objectPosition = `${this.focusX}% ${this.focusY}%`;
      this.#draw();
    });

    const end = (/** @type {PointerEvent} */ event) => {
      if (!drag || event.pointerId !== drag.pointerId) return;
      drag = null;
      wall.classList.remove('is-dragging');
      try {
        wall.releasePointerCapture(event.pointerId);
      } catch {
        /* no habia captura que soltar */
      }
      // El encuadre viaja con el pedido, así que hay que republicarlo.
      this.sync();
    };

    wall.addEventListener('pointerup', end);
    wall.addEventListener('pointercancel', end);
  }
}

if (!customElements.get('mural-wall-preview')) {
  customElements.define('mural-wall-preview', MuralWallPreview);
}
